import { NavigationMenuProps, NavigationRouteProps } from '@msc-b2c/interfaces/types';
import { Route, Routes } from 'react-router-dom';
import { NavigationRoute } from './NavigationRoute';
import { NavigationMenu } from './NavigationMenu';
import { useEffect, useMemo } from 'react';
import { LoginMode, NavigationRouteTypeEnum } from '@msc-b2c/interfaces/enums';
import {
  getActiveSession,
  navigationCreated,
  NavigationMenuState,
  useGetBookingQuery,
  RootState,
} from '@msc-b2c/shared-utils';
import { GetBookingMyMscResponse } from '@msc-b2c/interfaces/interfaces';
import { getDepartureDate, isPaymentEnabled, isXDaysBeforeDeparture } from '../utils/myAreaUtils';
import { enrichSessionWithContactInfo } from '../utils/contactInfoEnrichment';
import { getDeviceType } from '../utils/utils';
import { hideNavigationMenuSkeleton } from './utils/navigationMenuSkeleton';
import { useDispatch, useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';

export function NavigationMenuWrapper({
  navigationItems,
  allMyCruisesLink,
  noLinkedBookingLink,
  cartLink,
  checkoutLink,
  alreadyPurchasedLink,
  alreadyPurchasedSectionRouteMapping,
  menuExpandAriaLabel,
  menuCollapseAriaLabel,
}: Readonly<NavigationMenuProps>) {
  const dispatch = useDispatch();
  const {
    loginMode,
    goldenId: sessionGoldenId,
    bookingNumber: sessionBookingNumber = '',
  } = getActiveSession();
  const deviceType = getDeviceType();
  const activeBookingNumber = useSelector(
    (state: RootState) => state.myCruise.activeBookingNumber ?? null
  );
  const activeCruiseId = useSelector((state: RootState) => state.myCruise.activeCruiseId ?? null);
  const effectiveBookingNumber = activeBookingNumber || sessionBookingNumber;
  const hasBooking = Boolean(effectiveBookingNumber);

  // Enrich the full-login session with gender and mobilePhone from the customer
  // profile API. Fires once on mount; skipped if already populated or if another
  // component on the same page (e.g. CustomerInformationContainer) has already
  // initiated the same API call.
  useEffect(() => {
    if (loginMode === 'full' && sessionGoldenId) {
      enrichSessionWithContactInfo(sessionGoldenId);
    }
  }, []);

  // GET BOOKING API CALL
  const {
    data: booking,
    isLoading: isBookingLoading,
    isFetching: isBookingFetching,
  } = useGetBookingQuery(effectiveBookingNumber !== '' ? effectiveBookingNumber : skipToken);

  const isBookingPending =
    effectiveBookingNumber !== '' && (isBookingLoading || isBookingFetching) && !booking;

  /**
   * Filters and maps navigation items based on the booking and login mode:
   *    1. If the login mode is 'light', filter only the first/second level navigation items that have the 'isDisplayInLightLogin' flag set to true.
   *    2. If the login mode is 'full', filter only the first/second level navigation items that have the 'isDisplayInFullLogin' flag set to true.
   *    3. If the #daysBeforeDeparture <= #daysToDisableLightLoginOrHidePrepaid, filter only the second level navigation items that have the
   *        'hideXDaysBeforeDeparture' flag set to false.
   *
   * @param {GetBookingMyMscResponse | undefined} booking - The booking information.
   * @param {string} loginMode - The current login mode.
   * @param {NavigationRouteProps[]} navigationItems - The list of navigation items.
   * @returns {NavigationRouteProps[]} The filtered and mapped navigation items.
   */
  const useFilteredNavigationItems = (
    booking: GetBookingMyMscResponse | undefined,
    loginMode: string,
    navigationItems: NavigationRouteProps[]
  ): NavigationRouteProps[] => {
    return useMemo(() => {
      const bookingNumber = getActiveSession().bookingNumber ?? '';
      const isPaymentAvailable = isPaymentEnabled(booking);
      if ((!booking && !bookingNumber) || (booking && bookingNumber)) {
        const filteredNavigationItems = (navigationItems || [])
          .filter(
            (item) =>
              (loginMode === LoginMode.LIGHT
                ? item.isDisplayInLightLogin
                : item.isDisplayInFullLogin) &&
              !(item.routeType === NavigationRouteTypeEnum.CRUISE_PAYMENT && !isPaymentAvailable)
          )
          .map((item) => ({
            ...item,
            children: item.children
              ? item.children.filter((child) => {
                  return (
                    (loginMode === LoginMode.LIGHT
                      ? child.isDisplayInLightLogin
                      : child.isDisplayInFullLogin) &&
                    ((deviceType === 'mobile' && !child.hideInMobile) ||
                      (deviceType === 'tablet' && !child.hideInTablet) ||
                      (deviceType === 'desktop' && !child.hideInDesktop)) &&
                    (!child.hideXDaysBeforeDeparture ||
                      !isXDaysBeforeDeparture(getDepartureDate(booking))) &&
                    !(
                      child.routeType === NavigationRouteTypeEnum.CRUISE_PAYMENT &&
                      !isPaymentAvailable
                    )
                  );
                })
              : [],
          }));
        const navigationState: NavigationMenuState = {
          navigationItems: filteredNavigationItems,
          allMyCruisesLink,
          noLinkedBookingLink,
          cartLink,
          checkoutLink,
          alreadyPurchasedLink,
          alreadyPurchasedSectionRouteMapping,
        };

        dispatch(navigationCreated(navigationState));
        return filteredNavigationItems;
      }
      return [];
    }, [loginMode, navigationItems, booking, activeCruiseId]);
  };

  const filteredNavigationItems = useFilteredNavigationItems(
    booking,
    loginMode || LoginMode.FULL,
    navigationItems
  );

  const shouldRemoveSkeleton =
    filteredNavigationItems.length > 0 ||
    (!isBookingPending && (!effectiveBookingNumber || booking !== undefined));

  useEffect(() => {
    if (shouldRemoveSkeleton) {
      hideNavigationMenuSkeleton();
    }
  }, [shouldRemoveSkeleton]);

  const getResolvedRoute = (url: string) => {
    const parentPath = filteredNavigationItems
      ?.find((navItem) => url?.includes(navItem?.urlLink?.url?.replace('.html', '') ?? ''))
      ?.urlLink?.url?.replace('.html', '');
    return url?.replace(parentPath ?? '', '');
  };

  return (
    <>
      {filteredNavigationItems.length > 0 && (
        <NavigationMenu
          booking={booking}
          hasBooking={hasBooking}
          navigationItems={filteredNavigationItems}
          allMyCruisesLink={allMyCruisesLink}
          noLinkedBookingLink={noLinkedBookingLink}
          cartLink={cartLink}
          checkoutLink={checkoutLink}
          alreadyPurchasedLink={alreadyPurchasedLink}
          alreadyPurchasedSectionRouteMapping={alreadyPurchasedSectionRouteMapping}
          menuExpandAriaLabel={menuExpandAriaLabel}
          menuCollapseAriaLabel={menuCollapseAriaLabel}
        />
      )}
      <Routes>
        <Route
          path={'/*'}
          element={null}
        />
        {loginMode === LoginMode.FULL && (
          <Route
            path={getResolvedRoute(allMyCruisesLink?.url ?? '')}
            element={<NavigationRoute {...allMyCruisesLink} />}
          />
        )}
        {loginMode === LoginMode.FULL && (
          <Route
            path={getResolvedRoute(noLinkedBookingLink?.url ?? '')}
            element={<NavigationRoute {...noLinkedBookingLink} />}
          />
        )}
        <Route
          path={getResolvedRoute(cartLink?.url ?? '')}
          element={<NavigationRoute {...cartLink} />}
        />
        <Route
          path={getResolvedRoute(checkoutLink?.url ?? '')}
          element={<NavigationRoute {...checkoutLink} />}
        />
        {filteredNavigationItems?.map((item: NavigationRouteProps) =>
          item?.children?.map((navigationRoute: NavigationRouteProps, index: number) => {
            const childRoute = navigationRoute?.urlLink?.url?.replace(
              item?.urlLink?.url?.replace('.html', '') ?? '',
              ''
            );
            return (
              <Route
                key={`${navigationRoute?.label}-${index}`}
                path={childRoute}
                element={<NavigationRoute {...navigationRoute?.urlLink} />}
              />
            );
          })
        )}
      </Routes>
    </>
  );
}

NavigationMenuWrapper.aemResourceType = 'b2c-website-foundation/components/mymsc/navigationmenu';

export default NavigationMenuWrapper;
